import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Search,
  Tag,
  X,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Edit,
  Plus,
  Filter,
  Layers,
  ChevronDown,
  ChevronRight,
  BarChart3,
  DollarSign,
  ArrowUpDown,
  Loader2,
  Trash2,
  Pencil,
  Check,
} from 'lucide-react';
import { DateRangePicker } from '../ui';
import Button from '@/components/ui/Button';
import { useAttributionTagStore } from '@/stores/attributionTagStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import usePermissionsStore from '@/stores/permissionsStore';
import { AttributionTagStatus, TagGroupMember } from '@/types/attributionTag';

// The store's attribution list entry (group or solo tag).
interface ListEntry {
  kind: 'group' | 'tag';
  effectiveTagId: string;
  groupId: string | null;
  memberCount: number;
  hashtagId: number | null;
  hashtag: string;
  description?: string | null;
  status: string;
  postCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalEmv: string;
  totalEmvValue: number;
  previousEmvValue: number;
  accountCount: number;
}

// Kept for the parent's edit-drawer prop shape (solo tags only).
interface TagData {
  hashtagId: number;
  hashtag: string;
  description?: string | null;
  status: string;
  postCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalEmv: string;
  totalEmvValue: number;
  previousEmvValue: number;
  accountCount: number;
}

interface DateRange {
  start: string;
  end: string;
}

type PeriodOpt = 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';

interface AttributionTagsListProps {
  onCreateClick: () => void;
  onEditClick: (tag: TagData) => void;
  onTagSelect?: (
    hashtagId: number,
    hashtag: string,
    period: PeriodOpt,
    customDateRange?: DateRange | null
  ) => void;
  onGroupSelect?: (
    groupId: string,
    groupName: string,
    period: PeriodOpt,
    customDateRange?: DateRange | null
  ) => void;
}

// Deterministic per-name color (matches the Brand Value card pattern).
const PALETTE = [
  { avatar: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500' },
  { avatar: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-500' },
  { avatar: 'bg-purple-100 text-purple-700', bar: 'bg-purple-500' },
  { avatar: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' },
  { avatar: 'bg-rose-100 text-rose-700', bar: 'bg-rose-500' },
  { avatar: 'bg-cyan-100 text-cyan-700', bar: 'bg-cyan-500' },
  { avatar: 'bg-indigo-100 text-indigo-700', bar: 'bg-indigo-500' },
  { avatar: 'bg-teal-100 text-teal-700', bar: 'bg-teal-500' },
  { avatar: 'bg-fuchsia-100 text-fuchsia-700', bar: 'bg-fuchsia-500' },
  { avatar: 'bg-lime-100 text-lime-700', bar: 'bg-lime-500' },
];
const styleFor = (name: string) =>
  PALETTE[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(2)}K` : `$${(n || 0).toFixed(2)}`;
const fmtNum = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${Math.round(n || 0)}`;
const initials = (s: string) =>
  s.replace(/^[@#]/, '').slice(0, 2).toUpperCase() || '?';

const CARDS_PER_PAGE = 24;

const AttributionTagsList: React.FC<AttributionTagsListProps> = ({
  onCreateClick,
  onEditClick,
  onTagSelect,
  onGroupSelect,
}) => {
  const {
    attributionTagsAnalytics,
    isAttributionAnalyticsLoading,
    loadAttributionTagsAnalytics,
    loadTagGroupDetail,
    createTagGroup,
    renameTagGroup,
    addTagGroupMembers,
    removeTagGroupMembers,
    deleteTagGroup,
  } = useAttributionTagStore();

  const { selectedCustomer } = useGlobalCustomerStore();
  const { permissions } = usePermissionsStore();

  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<AttributionTagStatus | ''>('');
  const [sortBy, setSortBy] = useState<'posts' | 'engagement' | 'emv'>('emv');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOpt>('custom');
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 364);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  });

  // Group expander state
  const [expanded, setExpanded] = useState<string | null>(null);
  const [members, setMembers] = useState<Record<string, TagGroupMember[]>>({});
  const [loadingMembers, setLoadingMembers] = useState<string | null>(null);

  // Group management: multi-select solo tags, create/add/rename/ungroup
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [newGroupName, setNewGroupName] = useState('');
  const [addToGroupId, setAddToGroupId] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    loadAttributionTagsAnalytics(selectedPeriod, customDateRange);
    setMembers({}); // drop cached member lists so expanders refetch
  }, [loadAttributionTagsAnalytics, selectedPeriod, customDateRange]);

  useEffect(() => {
    if (selectedCustomer?.id) {
      loadAttributionTagsAnalytics(selectedPeriod, customDateRange);
    }
  }, [selectedCustomer?.id, selectedPeriod, customDateRange, loadAttributionTagsAnalytics]);

  // Existing groups (for the "add to group" picker)
  const existingGroups = useMemo(
    () => (attributionTagsAnalytics?.tags || []).filter((t: any) => t.kind === 'group') as ListEntry[],
    [attributionTagsAnalytics]
  );

  const clearSelection = () => { setSelectedIds(new Set()); setNewGroupName(''); setAddToGroupId(''); };
  const toggleSelectId = (id: number) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleCreateGroup = async () => {
    if (selectedIds.size === 0) return;
    const name = newGroupName.trim() || pageItems.find((t) => t.hashtagId != null && selectedIds.has(t.hashtagId))?.hashtag || 'New Group';
    setBusy(true);
    const res = await createTagGroup(name, Array.from(selectedIds));
    setBusy(false);
    if (res) { clearSelection(); setSelectMode(false); reload(); }
  };

  const handleAddToGroup = async () => {
    if (selectedIds.size === 0 || !addToGroupId) return;
    setBusy(true);
    const res = await addTagGroupMembers(addToGroupId, Array.from(selectedIds));
    setBusy(false);
    if (res) { clearSelection(); setSelectMode(false); reload(); }
  };

  const handleUngroup = async (groupId: string) => {
    setBusy(true);
    const ok = await deleteTagGroup(groupId);
    setBusy(false);
    if (ok) reload();
  };

  const commitRename = async (groupId: string) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    setBusy(true);
    const res = await renameTagGroup(groupId, name);
    setBusy(false);
    setRenamingId(null);
    if (res) reload();
  };

  const handleRemoveMember = async (groupId: string, hashtagId: number) => {
    setBusy(true);
    const res = await removeTagGroupMembers(groupId, [hashtagId]);
    setBusy(false);
    if (res) {
      setMembers((m) => ({ ...m, [groupId]: (m[groupId] || []).filter((x) => x.hashtag_id !== hashtagId) }));
      reload();
    }
  };

  const tagsData = (attributionTagsAnalytics?.tags || []) as ListEntry[];

  const filtered = useMemo(() => {
    let out = [...tagsData];
    if (searchInput) {
      out = out.filter((t) => t.hashtag.toLowerCase().includes(searchInput.toLowerCase()));
    }
    if (statusFilter) {
      // Groups have no status; keep them visible when filtering solo tags.
      out = out.filter((t) => t.kind === 'group' || t.status === statusFilter);
    }
    out.sort((a, b) => {
      let c = 0;
      if (sortBy === 'emv') c = a.totalEmvValue - b.totalEmvValue;
      else if (sortBy === 'posts') c = a.postCount - b.postCount;
      else c = a.totalLikes + a.totalComments + a.totalShares + a.totalViews - (b.totalLikes + b.totalComments + b.totalShares + b.totalViews);
      return sortOrder === 'desc' ? -c : c;
    });
    return out;
  }, [tagsData, searchInput, statusFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filtered.length / CARDS_PER_PAGE);
  const pageItems = useMemo(
    () => filtered.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE),
    [filtered, currentPage]
  );

  const cycleSort = useCallback((field: typeof sortBy) => {
    if (sortBy === field) setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortOrder('desc'); }
    setCurrentPage(1);
  }, [sortBy]);

  const handleDateRangeChange = (range: DateRange | null, period: string) => {
    if (period === 'custom' && range) { setSelectedPeriod('custom'); setCustomDateRange(range); }
    else { setSelectedPeriod(period as PeriodOpt); setCustomDateRange(null); }
    setCurrentPage(1);
  };

  const selectTag = (hashtagId: number | null, label: string) => {
    if (hashtagId == null || !onTagSelect) return;
    onTagSelect(hashtagId, label, selectedPeriod, selectedPeriod === 'custom' ? customDateRange : null);
  };

  const toggleGroup = async (entry: ListEntry) => {
    if (!entry.groupId) return;
    const gid = entry.groupId;
    if (expanded === gid) { setExpanded(null); return; }
    setExpanded(gid);
    if (!members[gid]) {
      setLoadingMembers(gid);
      const detail = await loadTagGroupDetail(gid, {
        period: selectedPeriod,
        ...(selectedPeriod === 'custom' && customDateRange
          ? { customStartDate: customDateRange.start, customEndDate: customDateRange.end }
          : {}),
      });
      setMembers((m) => ({ ...m, [gid]: detail?.members || [] }));
      setLoadingMembers(null);
    }
  };

  const engagementRow = (t: ListEntry) => (
    <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-gray-600">
      <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3 text-gray-400" />{fmtNum(t.postCount)} posts</span>
      <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-purple-500" />{fmtNum(t.totalViews)}</span>
      <span className="flex items-center gap-1"><Heart className="h-3 w-3 text-rose-500" />{fmtNum(t.totalLikes)}</span>
      <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3 text-blue-500" />{fmtNum(t.totalComments)}</span>
      <span className="flex items-center gap-1"><Share2 className="h-3 w-3 text-green-500" />{fmtNum(t.totalShares)}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Attribution Tags</h2>
          <p className="text-xs text-gray-600">Tracked hashtags & grouped variants with performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setSelectMode((m) => !m); clearSelection(); }}
            className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              selectMode ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Layers className="h-3.5 w-3.5 mr-1.5" />
            {selectMode ? 'Done' : 'Group tags'}
          </button>
          <Button onClick={onCreateClick} size="xs">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Create Tag
          </Button>
        </div>
      </div>

      {/* Selection action bar (group management) */}
      {selectMode && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <span className="text-xs font-semibold text-indigo-700">
            {selectedIds.size} selected
          </span>
          <span className="text-[11px] text-indigo-500 mr-2">— tick the tags to combine as one brand/entity</span>
          <input
            type="text"
            placeholder="New group name (optional)"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            className="px-2.5 py-1.5 text-xs border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 w-56"
          />
          <button
            onClick={handleCreateGroup}
            disabled={selectedIds.size === 0 || busy}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            <Check className="h-3.5 w-3.5 mr-1" /> Create group
          </button>
          {existingGroups.length > 0 && (
            <>
              <span className="text-[11px] text-indigo-400">or</span>
              <select
                value={addToGroupId}
                onChange={(e) => setAddToGroupId(e.target.value)}
                className="px-2 py-1.5 text-xs border border-indigo-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Add to group…</option>
                {existingGroups.map((g) => <option key={g.groupId!} value={g.groupId!}>{g.hashtag}</option>)}
              </select>
              <button
                onClick={handleAddToGroup}
                disabled={selectedIds.size === 0 || !addToGroupId || busy}
                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
              >
                Add
              </button>
            </>
          )}
          <button onClick={clearSelection} className="ml-auto text-[11px] text-indigo-500 hover:text-indigo-700">Clear</button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search tags & groups..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
            className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-72"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setCurrentPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as AttributionTagStatus | ''); setCurrentPage(1); }}
            className="pl-8 pr-6 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Sort */}
        <div className="relative">
          <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <select
            value={sortBy}
            onChange={(e) => cycleSort(e.target.value as typeof sortBy)}
            className="pl-8 pr-6 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
          >
            <option value="emv">Sort: EMV</option>
            <option value="posts">Sort: Posts</option>
            <option value="engagement">Sort: Engagement</option>
          </select>
        </div>
        <button
          onClick={() => setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'))}
          className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}
        >
          {sortOrder === 'desc' ? '↓' : '↑'}
        </button>

        <div className="ml-auto">
          <DateRangePicker
            value={customDateRange}
            onChange={handleDateRangeChange}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!selectedCustomer?.id}
            selectedPeriod={selectedPeriod}
          />
        </div>
      </div>

      {/* Cards */}
      <div className="relative min-h-[200px]">
        {isAttributionAnalyticsLoading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-xl">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        {!isAttributionAnalyticsLoading && pageItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Tag className="mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tags found</h3>
            <p className="mt-1 text-xs text-gray-500">
              {searchInput || statusFilter ? 'Try adjusting your search or filter.' : 'Get started by creating an attribution tag.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {pageItems.map((t) => {
              const s = styleFor(t.hashtag);
              const isGroup = t.kind === 'group';
              const isOpen = isGroup && expanded === t.groupId;
              const selectable = selectMode && !isGroup && t.hashtagId != null;
              const isSelected = selectable && selectedIds.has(t.hashtagId as number);
              const isRenaming = isGroup && renamingId === t.groupId;
              return (
                <div
                  key={t.effectiveTagId}
                  className={`rounded-xl border bg-white overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${
                    isSelected ? 'border-indigo-400 ring-2 ring-indigo-400' : isGroup ? 'border-indigo-200 ring-1 ring-indigo-100' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`h-1 ${s.bar}`} />
                  <div
                    className={selectMode && isGroup ? 'p-4' : 'p-4 cursor-pointer'}
                    onClick={() => {
                      if (selectable) { toggleSelectId(t.hashtagId as number); return; }
                      if (selectMode && isGroup) return; // groups aren't selectable
                      if (isGroup) {
                        onGroupSelect?.(t.groupId as string, t.hashtag, selectedPeriod, selectedPeriod === 'custom' ? customDateRange : null);
                      } else {
                        selectTag(t.hashtagId, t.hashtag);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {selectable && (
                          <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-gray-300'}`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                        )}
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${s.avatar}`}>
                          {initials(t.hashtag)}
                        </div>
                        <div className="min-w-0">
                          {isRenaming ? (
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(t.groupId!); if (e.key === 'Escape') setRenamingId(null); }}
                              onBlur={() => commitRename(t.groupId!)}
                              className="text-sm font-semibold text-gray-900 border border-indigo-300 rounded px-1 py-0.5 w-full"
                            />
                          ) : (
                            <h3 className="text-sm font-semibold text-gray-900 truncate leading-tight" title={t.hashtag}>{t.hashtag}</h3>
                          )}
                          {isGroup ? (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-indigo-600">
                              <Layers className="h-2.5 w-2.5" /> Group · {t.memberCount}
                            </span>
                          ) : t.description ? (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-gray-500 truncate">
                              <Tag className="h-2.5 w-2.5" /> {t.description}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {!isGroup && !selectMode && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEditClick({ ...t, hashtagId: t.hashtagId as number }); }}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0"
                          title="Edit tag"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {isGroup && !isRenaming && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); setRenamingId(t.groupId!); setRenameValue(t.hashtag); }}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Rename group"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); if (confirm(`Ungroup "${t.hashtag}"? Its ${t.memberCount} tags become standalone again.`)) handleUngroup(t.groupId!); }}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Ungroup (delete group)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-1.5">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                      <span className="text-2xl font-bold text-emerald-600 leading-none">{fmtEMV(t.totalEmvValue)}</span>
                    </div>
                    <div className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-gray-400">Total EMV</div>

                    {engagementRow(t)}

                    {isGroup && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroup(t); }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        {isOpen ? 'Hide' : `View ${t.memberCount} tags`}
                      </button>
                    )}
                  </div>

                  {/* Expanded members */}
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-2">
                      {loadingMembers === t.groupId ? (
                        <div className="flex items-center gap-2 py-2 text-[11px] text-gray-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading variants…
                        </div>
                      ) : (members[t.groupId!] || []).length === 0 ? (
                        <div className="py-2 text-[11px] text-gray-400">No member tags.</div>
                      ) : (
                        <ul className="divide-y divide-gray-100">
                          {(members[t.groupId!] || []).map((m) => (
                            <li key={m.hashtag_id} className="flex items-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); selectTag(m.hashtag_id, m.hashtag_text); }}
                                className="flex-1 min-w-0 flex items-center justify-between py-1.5 text-left group"
                                title={`View ${m.hashtag_text}`}
                              >
                                <span className="text-[11px] font-medium text-gray-700 truncate group-hover:text-indigo-700">{m.hashtag_text}</span>
                                <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-indigo-500 flex-shrink-0" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveMember(t.groupId!, m.hashtag_id); }}
                                className="ml-1 p-0.5 text-gray-300 hover:text-red-600 flex-shrink-0"
                                title="Remove from group"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer: count + pagination */}
      {pageItems.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-600">
            Showing <span className="font-medium">{filtered.length}</span> of <span className="font-medium">{tagsData.length}</span> entries
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
              </button>
              <span className="text-xs text-gray-600 px-2">Page {currentPage} / {totalPages}</span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttributionTagsList;
